import { useCallback, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
    BUDGET_OPTIMIZER,
    CONFIRM_BUDGET_DECISION,
    DISMISS_BUDGET_DECISION,
    RECHECK_BUDGET_DECISION,
    RESOLVE_PARTIAL_BUDGET_DECISION,
    SET_BUDGET_OPTIMIZER_MODE,
    UNDO_BUDGET_DECISION,
} from '@/graphql/budget-optimizer-queries'
import { showNotification } from '@/utils/notifications'
import { ACTIONS, PARTIAL_ACTION_VALUES, refusalText, successText } from '@/utils/budget-optimizer'

const notify = (message, type = 'success', duration = 6000) =>
    showNotification(message, type, duration, null, null, true, true)

/**
 * The budget optimizer of one ad campaign: what the panel shows and what its owner can do. The server decides every
 * action; a refusal is shown with its reason and the panel is read again.
 * @param {string} adCampaignId
 * @param {{ skip?: boolean }} [options]
 */
export const useBudgetOptimizer = (adCampaignId, { skip = false } = {}) => {
    const [busy, setBusy] = useState(false)
    const { data, loading, error, refetch } = useQuery(BUDGET_OPTIMIZER, {
        variables: { adCampaignId },
        skip: skip || !adCampaignId,
        fetchPolicy: 'cache-and-network',
    })
    const [setMode] = useMutation(SET_BUDGET_OPTIMIZER_MODE)
    const [confirm] = useMutation(CONFIRM_BUDGET_DECISION)
    const [dismiss] = useMutation(DISMISS_BUDGET_DECISION)
    const [undo] = useMutation(UNDO_BUDGET_DECISION)
    const [resolvePartial] = useMutation(RESOLVE_PARTIAL_BUDGET_DECISION)
    const [recheck] = useMutation(RECHECK_BUDGET_DECISION)

    const optimizer = data?.AdCampaign?.budgetOptimizer || null

    const run = useCallback(
        async (send, success, { movesBudget = false } = {}) => {
            setBusy(true)
            let succeeded
            try {
                const response = await send()
                const result = Object.values(response?.data || {})[0]
                if (result && result.ok === false) {
                    notify(refusalText(result), 'error', 10000)
                    succeeded = false
                } else {
                    // What the action did is told by the state the server answers with, not by the click
                    const text = movesBudget ? successText(success, result?.optimizer?.decision?.status) : success
                    if (text) notify(text)
                    succeeded = true
                }
            } catch (caught) {
                notify(caught?.message || 'This could not be done. Try again.', 'error', 10000)
                return false
            } finally {
                setBusy(false)
            }
            // A failed read afterwards is not a failed action: the panel keeps what it showed until the next read
            try {
                await refetch()
            } catch {
                // nothing to add: the action itself was answered above
            }
            return succeeded
        },
        [refetch],
    )

    const changeMode = useCallback(
        (mode) => run(() => setMode({ variables: { adCampaignId, mode } }), 'The optimizer mode was saved.'),
        [adCampaignId, run, setMode],
    )

    const act = useCallback(
        (action, decisionId) => {
            const variables = { adCampaignId, decisionId }
            switch (action) {
                case ACTIONS.APPLY:
                    return run(() => confirm({ variables }), 'The budget move was applied.', { movesBudget: true })
                case ACTIONS.DISMISS:
                    return run(() => dismiss({ variables }), 'The suggestion was dismissed.')
                case ACTIONS.UNDO:
                    return run(() => undo({ variables }), 'The budget move was undone.', { movesBudget: true })
                case ACTIONS.CHECK_AGAIN:
                    return run(() => recheck({ variables }), 'Checked with Meta.')
                case ACTIONS.RETRY_RAISE:
                case ACTIONS.PUT_BACK:
                case ACTIONS.ACCEPT_LOWER_TOTAL:
                    return run(
                        () => resolvePartial({ variables: { ...variables, action: PARTIAL_ACTION_VALUES[action] } }),
                        'Done.',
                        { movesBudget: true },
                    )
                default:
                    return Promise.resolve(false)
            }
        },
        [adCampaignId, confirm, dismiss, recheck, resolvePartial, run, undo],
    )

    return { optimizer, loading, error, busy, changeMode, act }
}
